import { computed, ref, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import { setItemToLocalStorage, getItemFromLocalStorage } from '../utils/persistenceStorage';
import { setCSSStyles } from '../utils/setCSSStyles';
import { debounce } from '../utils/debounce';
import cssColorVariables from '../assets/cssColorVariables';

export const useProjectSettingsStore = defineStore('projectSettings', () => {
  const route = useRoute();

  // State
  const customPageTitle = ref<string>('');

  const screenWidth = ref(window.innerWidth);
  
  const updateScreenWidth = debounce(() => {
      screenWidth.value = window.innerWidth;
  }, 200);

  window.addEventListener('resize', updateScreenWidth);

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateScreenWidth);
  });

  const metaTitle = computed((): string => !!route.meta?.title ? String(route.meta.title) : '');

  const pageTitle = computed((): string => customPageTitle.value || metaTitle.value);

  const isMobile = computed((): boolean => screenWidth.value <= 768);

  const appTheme = ref<'light' | 'dark'>(getItemFromLocalStorage('appTheme') || 'light');

  const baseColors = computed((): Record<PropertyKey, string> => (
    (cssColorVariables as any)[appTheme.value]
  ));

  const customColors = ref<Record<PropertyKey, string>>(
    {}
  );

  const colors = computed((): Record<PropertyKey, string> => ({
    ...baseColors.value,
    ...customColors.value
  }));

  // Actions
  const toggleAppTheme = (): void => {
    const theme = appTheme.value === 'light' ? 'dark' : 'light';
    
    setItemToLocalStorage('appTheme', theme);
    appTheme.value = theme;
    
    setCSSStyles(colors);
  };

  const setPageTitle = (title: string) => {
    customPageTitle.value = title;
  }

  return {
    appTheme,
    colors,
    toggleAppTheme,
    screenWidth,
    isMobile,
    pageTitle,
    metaTitle,
    setPageTitle,
  }
});